"use client";

import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  AuthError,
  OAuthCredential,
} from "firebase/auth";
import { auth } from "./config";

/**
 * Firebase authentication response type
 */
export interface FirebaseAuthResponse {
  user: {
    uid: string;
    email: string | null;
    displayName: string | null;
    photoURL: string | null;
  };
  token: string;
}

/**
 * Sign in with Google using Firebase (Popup method)
 */
export async function signInWithGoogle(): Promise<OAuthCredential | AuthError> {
  try {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: "select_account",
    });

    const result = await signInWithPopup(auth, provider);

    // console.log({ result });

    const credential = GoogleAuthProvider.credentialFromResult(result);

    if (!credential) throw new Error("Invalid token");

    return credential;
  } catch (error) {
    return error as AuthError;
  }
}

/**
 * Sign in with Google using Firebase (Redirect method)
 * This is better for mobile devices
 */
export function signInWithGoogleRedirect() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({
    prompt: "select_account",
  });

  return signInWithRedirect(auth, provider);
}

/**
 * Handle the redirect result from Google sign-in
 */
export async function handleGoogleRedirectResult(): Promise<
  FirebaseAuthResponse | AuthError | null
> {
  try {
    const result = await getRedirectResult(auth);

    if (!result) {
      return null; // No redirect result
    }

    // Get the ID token
    const token = await result.user.getIdToken();

    return {
      user: {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
      },
      token,
    };
  } catch (error) {
    return error as AuthError;
  }
}
